/**
 * Dealer Desk: why a trade can't open, and what the dealer can do about it (DEALER_OPEN_REFUSALS_PLAN.md).
 *
 * - `checkDeskOpen` runs the server's open checks on what the Open position pop-up already has (the
 *   instrument, the live price, the account's free margin, the client's restrictions and caps, a master's
 *   caps), for Sell and for Buy, so Sell / Buy are off until the trade can open.
 * - `explainRefusal` turns a refusal from the server (its code and message) into the same note. The API
 *   adds it to a refused dealer order as `help`, so the Dealer Desk and the CRM show the same words.
 *
 * Words are for the dealer: "the client", never "you". Every note says what is wrong, with the figures,
 * and what the dealer can do: a fix the form can apply in one click, or where the rest is changed.
 */
import { D } from './decimal.js';
import { fmt, fmtUsd } from './format.js';
import { initialMargin, leverageCap, lotsToQty, maxNotional, notionalOf, openFeeFor, validateTpSl } from './trading-math.js';
const NUM = /^\d+(\.\d+)?$/;
const num = (x) => (x != null && NUM.test(String(x).trim()) && D.from(String(x).trim()).isPos() ? D.from(String(x).trim()) : null);
const sideName = (s) => (s === 'long' ? 'Buy' : 'Sell');
const dpOf = (step) => Math.max(0, (D.from(step).toString().split('.')[1] ?? '').length);
/** A whole-dollar amount when it is one: $500,000 rather than $500,000.00. */
const usd = (x) => (D.from(x).round(0).eq(D.from(x)) ? fmtUsd(x, 0) : fmtUsd(x));
const UTC_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
function inTime(ms) {
    const min = Math.max(1, Math.round(ms / 60_000));
    if (min < 60)
        return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 48)
        return `${h} h ${min % 60} min`;
    return `${Math.round(h / 24)} days`;
}
/** Lots that fit an amount in USD at a price: whole steps, rounded down. */
function lotsFor(amount, price, f) {
    const unit = price.mul(f.inst.lotSize).mul(f.pointValue);
    if (!unit.isPos())
        return D.ZERO;
    return amount.div(unit, 'down').div(f.inst.lotStep, 'down').round(0, 'down').mul(f.inst.lotStep);
}
/** A fix to a size: in lots when the form is by lots, in USDT when it is by amount. */
function sizeFix(form, f, lots, amount) {
    if (num(form.lots)) {
        if (lots.lt(f.inst.minLots))
            return null;
        const v = lots.toFixed(dpOf(f.inst.lotStep));
        return { kind: 'lots', value: v, label: `Set ${v} lots` };
    }
    const v = amount.round(2, 'down');
    return v.isPos() ? { kind: 'notional', value: v.toString(), label: `Set ${usd(v)}` } : null;
}
/** What stops the trade on both sides: the master's instruments, the client's restrictions, the market, the price. */
function accountProblem(f) {
    const sym = f.inst.symbol;
    const m = f.master;
    if (m && m.instruments.length && !m.instruments.includes(sym)) {
        return {
            code: 'instrument_not_allowed',
            title: `${sym} isn't on ${m.name}'s list`,
            text: `${m.name} trades only ${m.instruments.join(', ')}, so its followers are never copied into ${sym}.`,
            fixes: [{ kind: 'link', to: 'master', label: `Edit ${m.name}'s instruments`, note: 'Admin → Masters → Settings' }],
        };
    }
    const c = f.client;
    const why = c?.reason ? ` (${c.reason})` : '';
    if (c?.tradingStatus === 'blocked') {
        return {
            code: 'trading_blocked',
            title: 'Trading is blocked for this client',
            text: `Their trading is blocked${why}. New trades are refused, from the desk too.`,
            fixes: [{ kind: 'link', to: 'client', label: 'Open the client in the CRM', note: 'compliance can change Trading status' }],
        };
    }
    if (c?.tradingStatus === 'close_only') {
        return {
            code: 'trading_close_only',
            title: 'This client can only close trades',
            text: `Their trading is set to close-only${why}. New trades are refused, from the desk too. Closing and TP / SL changes still work.`,
            fixes: [{ kind: 'link', to: 'client', label: 'Open the client in the CRM', note: 'compliance can change Trading status' }],
        };
    }
    if (!f.open) {
        const now = f.now ?? Date.now();
        const when = f.opensAt ? `It opens ${UTC_FMT.format(f.opensAt).replace(',', '')} UTC, in ${inTime(f.opensAt - now)}. ` : '';
        return {
            code: 'market_closed',
            title: `${sym}'s market is closed`,
            text: `${when}Neither a market nor a pending order can be placed while it is closed.`,
            fixes: [],
            note: 'Sell / Buy come back by themselves at the open.',
        };
    }
    if (f.paused || f.bid == null || f.ask == null) {
        return {
            code: 'price_paused',
            title: `No live price for ${sym} right now`,
            text: 'Every order needs a live price, so Sell / Buy stay off until it is back, usually within seconds.',
            fixes: [],
            note: 'Still off after a few minutes? Tell an admin: the price feed needs a look.',
        };
    }
    return null;
}
/** The volume and the leverage, the same for both sides. */
function sizeProblem(form, f, lev) {
    const i = f.inst;
    const lots = num(form.lots);
    if (lots) {
        const ldp = dpOf(i.lotStep);
        if (lots.lt(i.minLots)) {
            const min = D.from(i.minLots).toFixed(ldp);
            return {
                code: 'below_min_lots',
                title: 'Below the minimum volume',
                text: `${lots.toString()} lots is below ${i.symbol}'s minimum of ${min} lots.`,
                field: 'size',
                fixes: [{ kind: 'lots', value: min, label: `Set ${min} lots` }],
            };
        }
        if (!lots.div(i.lotStep).round(0).mul(i.lotStep).eq(lots)) {
            const lo = lots.div(i.lotStep, 'down').round(0, 'down').mul(i.lotStep);
            const hi = lo.add(i.lotStep);
            const fixes = [];
            if (lo.gte(i.minLots))
                fixes.push({ kind: 'lots', value: lo.toFixed(ldp), label: `Set ${lo.toFixed(ldp)} lots` });
            fixes.push({ kind: 'lots', value: hi.toFixed(ldp), label: `Set ${hi.toFixed(ldp)} lots` });
            return { code: 'bad_lot_step', title: `Volume moves in steps of ${D.from(i.lotStep).toString()} lots`, text: `${lots.toString()} lots isn't a whole number of steps.`, field: 'size', fixes };
        }
    }
    if (lev < i.minLeverage) {
        return { code: 'bad_leverage', title: `Leverage below ${i.symbol}'s minimum`, text: `${i.symbol} takes 1:${i.minLeverage} to 1:${i.maxLeverage}.`, field: 'leverage', fixes: [{ kind: 'leverage', value: i.minLeverage, label: `Use 1:${i.minLeverage}` }] };
    }
    const m = f.master;
    if (m?.maxLeverage && lev > m.maxLeverage) {
        const to = Math.min(m.maxLeverage, i.maxLeverage);
        return {
            code: 'leverage_above_max',
            title: `Leverage above ${m.name}'s cap`,
            text: `Leverage on ${m.name} is capped at 1:${m.maxLeverage}.`,
            field: 'leverage',
            fixes: [
                { kind: 'leverage', value: to, label: `Use 1:${to}` },
                { kind: 'link', to: 'master', label: `Change ${m.name}'s settings`, note: 'Admin → Masters → Settings' },
            ],
        };
    }
    if (lev > i.maxLeverage) {
        const to = leverageCap(i.maxLeverage, f.client?.maxLeverage);
        return { code: 'bad_leverage', title: `Leverage above ${i.symbol}'s maximum`, text: `${i.symbol} takes up to 1:${i.maxLeverage}.`, field: 'leverage', fixes: [{ kind: 'leverage', value: to, label: `Use 1:${to}` }] };
    }
    const cap = f.client?.maxLeverage;
    if (cap != null && lev > cap) {
        return {
            code: 'leverage_above_max',
            title: "Leverage above this client's cap",
            text: `This client's leverage is capped at 1:${cap} (set in the CRM). ${i.symbol} allows up to 1:${i.maxLeverage}.`,
            field: 'leverage',
            fixes: [{ kind: 'leverage', value: cap, label: `Use 1:${cap}` }],
            note: 'To raise the cap: CRM → client → Max leverage.',
        };
    }
    return null;
}
/** The order's value against the minimum and the max order, the margin, and TP / SL: each side on its own price. */
function sideProblem(side, form, f, lev) {
    const i = f.inst;
    const live = side === 'long' ? f.ask : f.bid;
    const price = num(form.price) ?? (live == null ? null : D.from(live));
    if (!price || !price.isPos())
        return null;
    const lots = num(form.lots);
    const value = lots ? notionalOf(lotsToQty(lots, i.lotSize), price, f.pointValue) : num(form.notional);
    if (!value)
        return null;
    const px = fmt(price, i.priceDecimals);
    const what = lots ? `${lots.toFixed(dpOf(i.lotStep))} lots at ${px} is a ${usd(value.round(2))} order` : `A ${usd(value)} order`;
    if (value.lt(i.minNotional)) {
        const least = lotsFor(D.from(i.minNotional), price, f).add(i.lotStep);
        const fix = sizeFix(form, f, D.max(least, D.from(i.minLots)), D.from(i.minNotional));
        return { code: 'below_min', title: 'Below the minimum order', text: `${what}. ${i.symbol}'s minimum order is ${usd(i.minNotional)}.`, field: 'size', fixes: fix ? [fix] : [] };
    }
    const m = f.master;
    const limit = m ? (m.maxOrder == null ? null : D.from(m.maxOrder)) : D.from(i.maxNotional);
    if (limit && value.gt(limit)) {
        const fits = lotsFor(limit, price, f);
        const fix = sizeFix(form, f, fits, limit);
        const at = lots && fits.gte(i.minLots) ? `: ${fits.toFixed(dpOf(i.lotStep))} lots at this rate` : '';
        if (m) {
            return {
                code: 'above_max',
                title: `Above ${m.name}'s max order`,
                text: `${what}, above ${m.name}'s limit of ${usd(limit)} in one order${at}.`,
                field: 'size',
                fixes: [...(fix ? [fix] : []), { kind: 'link', to: 'master', label: `Change ${m.name}'s max order`, note: 'Admin → Masters → Settings' }],
            };
        }
        return {
            code: 'above_max',
            title: `Above ${i.symbol}'s maximum order`,
            text: `${what}. ${i.symbol} takes at most ${usd(limit)} in one order${at}.`,
            field: 'size',
            fixes: [...(fix ? [fix] : []), { kind: 'link', to: 'instruments', label: `Change ${i.symbol}'s max order`, note: 'Admin → Instruments · applies to every client' }],
        };
    }
    if (f.freeMargin != null && !f.offsets?.[side]) {
        const free = D.from(f.freeMargin);
        const margin = initialMargin(value, lev);
        const fee = openFeeFor(value, i.feeRate, i.feeFlat);
        const need = margin.add(fee);
        if (need.gt(free)) {
            const fixes = [];
            const fitN = maxNotional(free, lev, i.feeRate, i.feeFlat);
            const fix = sizeFix(form, f, lotsFor(fitN, price, f), fitN);
            if (fix && fitN.gte(i.minNotional))
                fixes.push(fix);
            // The lowest leverage that carries it, within the instrument's, the client's and a master's caps.
            const room = free.sub(fee);
            const cap = Math.min(leverageCap(i.maxLeverage, f.client?.maxLeverage), m?.maxLeverage ?? Infinity);
            if (room.isPos()) {
                const want = Math.max(lev + 1, Math.ceil(value.div(room, 'up').toNumber()));
                if (want <= cap && initialMargin(value, want).add(fee).lte(free))
                    fixes.push({ kind: 'leverage', value: want, label: `Use 1:${want}` });
            }
            if (!m)
                fixes.push({ kind: 'link', to: 'money', label: 'Add funds', note: 'Money: deposit or credit' });
            const sized = lots ? `${lots.toFixed(dpOf(i.lotStep))} lots ${i.symbol}` : `${usd(value)} of ${i.symbol}`;
            return {
                code: 'insufficient_funds',
                title: 'Not enough free margin',
                text: `${sized} at 1:${lev} needs ${fmtUsd(need)} (margin ${fmtUsd(margin)} + fee ${fmtUsd(fee)}). The ${m ? 'master' : 'client'} has ${fmtUsd(free)} free margin.`,
                field: 'size',
                fixes,
            };
        }
    }
    for (const k of ['tp', 'sl']) {
        const level = num(form[k]);
        if (!level)
            continue;
        if (!validateTpSl(side, price, k === 'tp' ? level : null, k === 'sl' ? level : null))
            continue;
        const name = k === 'tp' ? 'Take profit' : 'Stop loss';
        const must = (side === 'long') === (k === 'tp') ? 'above' : 'below';
        const is = level.gt(price) ? 'above' : level.eq(price) ? 'at' : 'below';
        const other = side === 'long' ? 'short' : 'long';
        const otherPx = num(form.price) ?? (other === 'long' ? f.ask : f.bid);
        const fine = otherPx != null && !validateTpSl(other, otherPx, k === 'tp' ? level : null, k === 'sl' ? level : null);
        return {
            code: 'invalid_tpsl',
            title: `${name} is on the wrong side for a ${sideName(side)}`,
            text: `${name} ${fmt(level, i.priceDecimals)} is ${is} ${px}. For a ${sideName(side)} it must be ${must} the entry.${fine ? ` It is right for a ${sideName(other)}, so ${sideName(other)} stays on.` : ''}`,
            field: k,
            fixes: [{ kind: 'clear', field: k, label: `Clear ${name.toLowerCase()}` }],
            note: `Or drag the ${k.toUpperCase()} line on the chart.`,
        };
    }
    return null;
}
/** Why Sell and why Buy can't open now, from what the pop-up has; null for a side that can. Incomplete input gives null (the form says what to fill). */
export function checkDeskOpen(form, f) {
    const both = accountProblem(f);
    if (both)
        return { long: both, short: both };
    if (!/^\d+$/.test(String(form.leverage).trim()) || Number(form.leverage) < 1)
        return { long: null, short: null };
    const lev = Number(form.leverage);
    const size = sizeProblem(form, f, lev);
    if (size)
        return { long: size, short: size };
    return { long: sideProblem('long', form, f, lev), short: sideProblem('short', form, f, lev) };
}
/** The server refusals the pop-up's own check can explain with its figures. */
const CHECKED = {
    above_max: ['above_max'],
    below_min: ['below_min'],
    below_min_lots: ['below_min_lots', 'below_min'],
    bad_lot_step: ['bad_lot_step'],
    bad_request: ['bad_leverage'],
    leverage_above_max: ['leverage_above_max'],
    insufficient_funds: ['insufficient_funds'],
    invalid_tpsl: ['invalid_tpsl'],
    market_closed: ['market_closed'],
    trading_blocked: ['trading_blocked'],
    trading_close_only: ['trading_close_only'],
    instrument_not_allowed: ['instrument_not_allowed'],
    no_price: ['price_paused'],
    stale_price: ['price_paused'],
    price_paused: ['price_paused'],
};
/** A client-worded server message, for the dealer: "you" is the client. */
const forDealer = (m) => m
    .replace(/\bThe most you can hold\b/g, 'The most the client can hold')
    .replace(/\byou can\b/g, 'the client can')
    .replace(/\bYou can\b/g, 'The client can')
    .replace(/\byour\b/g, "the client's")
    .replace(/\bYour\b/g, "The client's")
    .replace(/\bsince you saw it\b/g, 'since it was quoted');
const FALLBACK_FIX = { kind: 'copy', label: 'Copy details for support', note: 'code, account, instrument, figures, time' };
/**
 * The note for a refusal from the server. With the facts and the form (the API has both), a refusal the
 * pop-up's check knows comes with its figures and one-click fixes; otherwise the server's own words, for the
 * dealer, with what can be done. Anything unknown says that nothing was opened and offers the details.
 */
export function explainRefusal(code, message, ctx) {
    if (ctx && CHECKED[code]) {
        const pre = checkDeskOpen(ctx.form, ctx.facts)[ctx.side];
        if (pre && CHECKED[code].includes(pre.code))
            return { ...pre, code };
    }
    const msg = forDealer(message);
    switch (code) {
        case 'above_max':
            return { code, title: 'Above the maximum order', text: msg, field: 'size', fixes: [{ kind: 'link', to: 'instruments', label: "Change the instrument's max order", note: 'Admin → Instruments · applies to every client' }] };
        case 'below_min':
        case 'below_min_lots':
        case 'bad_lot_step':
        case 'too_small':
            return { code, title: 'Below the minimum order', text: msg, field: 'size', fixes: [] };
        case 'leverage_above_max':
            return { code, title: 'Leverage above the cap', text: msg, field: 'leverage', fixes: [] };
        case 'insufficient_funds':
            return { code, title: 'Not enough free margin', text: msg, field: 'size', fixes: [{ kind: 'link', to: 'money', label: 'Add funds', note: 'Money: deposit or credit' }] };
        case 'invalid_tpsl':
            return { code, title: 'Take profit or stop loss on the wrong side', text: msg, fixes: [{ kind: 'clear', field: 'tp', label: 'Clear take profit' }, { kind: 'clear', field: 'sl', label: 'Clear stop loss' }] };
        case 'tpsl_crossed':
            return {
                code,
                title: 'The market has already passed this TP or SL',
                text: `${msg} The trade would close as soon as it opened.`,
                fixes: [{ kind: 'clear', field: 'tp', label: 'Clear take profit' }, { kind: 'clear', field: 'sl', label: 'Clear stop loss' }],
            };
        case 'invalid_pending_price':
            return { code, title: 'The market moved past this rate', text: `${msg} The price moved between typing the rate and sending the order.`, field: 'price', fixes: [{ kind: 'current_rate', label: 'Use the current rate' }] };
        case 'market_closed':
            return { code, title: 'The market is closed', text: `${msg}. Neither a market nor a pending order can be placed while it is closed.`, fixes: [], note: 'Place it after the open.' };
        case 'no_price':
        case 'stale_price':
        case 'price_paused':
            return { code, title: 'No live price right now', text: `${msg}. Every order needs a live price; it is usually back within seconds.`, fixes: [], note: 'Still off after a few minutes? Tell an admin: the price feed needs a look.' };
        case 'trading_blocked':
            return { code, title: 'Trading is blocked for this client', text: `${msg.replace(/\s*Contact support\.?$/, '')} New trades are refused, from the desk too.`, fixes: [{ kind: 'link', to: 'client', label: 'Open the client in the CRM', note: 'compliance can change Trading status' }] };
        case 'trading_close_only':
            return { code, title: 'This client can only close trades', text: `${msg} New trades are refused, from the desk too.`, fixes: [{ kind: 'link', to: 'client', label: 'Open the client in the CRM', note: 'compliance can change Trading status' }] };
        case 'account_under_review':
            return {
                code,
                title: "The client's account isn't approved yet",
                text: 'They signed up on their own and their account is still under review (KYC). Real-money trades open once it is approved.',
                fixes: [{ kind: 'link', to: 'kyc', label: 'Open the KYC review' }],
            };
        case 'instrument_not_allowed':
            return { code, title: "Not on this master's list", text: `${msg}: the master trades only its own list of instruments.`, fixes: [{ kind: 'link', to: 'master', label: "Edit the master's instruments", note: 'Admin → Masters → Settings' }] };
        case 'master_closed':
            return { code, title: 'This master account is closed', text: 'A closed master takes no new trades.', fixes: [] };
        case 'entry_too_old':
        case 'before_history':
        case 'time_in_future':
        case 'entry_not_allowed':
        case 'entry_market_only':
            return { code, title: "This chart entry can't be used", text: msg, field: 'price', fixes: [], note: 'Pick another point on the chart.' };
        case 'transfer_market_only':
        case 'transfer_not_allowed':
            return { code, title: "This trade can't be a transferred trade", text: msg, fixes: [] };
        case 'not_your_client':
            return { code, title: "This client isn't one of yours", text: message, fixes: [], note: 'Ask their owner or a dealer to place it.' };
        case 'not_permitted':
            return { code, title: "Your role can't do this", text: message, fixes: [], note: "Ask an admin to change your role's permissions." };
        case 'meme_halted':
            return { code, title: 'Trading in this coin is paused', text: msg, fixes: [], note: 'Sell / Buy can be tried again once the pause ends.' };
        case 'meme_close_only':
            return { code, title: 'This coin is close-only', text: msg, fixes: [] };
        case 'meme_not_tradable':
        case 'meme_delisted':
            return { code, title: "This coin can't be traded", text: msg, fixes: [] };
        case 'meme_price_frozen':
            return { code, title: "The coin's price sources disagree", text: msg, fixes: [], note: 'New positions open again once they agree.' };
        case 'meme_client_cap':
            return { code, title: "Above the client's limit for this coin", text: msg, field: 'size', fixes: [] };
        case 'meme_house_cap':
            return { code, title: "We aren't taking more on this side of the coin", text: msg, fixes: [] };
        case 'price_moved':
            return { code, title: 'The price moved too far', text: msg, fixes: [] };
        default:
            return { code, title: 'The server refused this trade', text: `“${message}” (${code}). Nothing was opened and the account is unchanged.`, fixes: [FALLBACK_FIX] };
    }
}
//# sourceMappingURL=desk-refusals.js.map